import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../Typography';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RichTextProps {
  children: ReactNode;
  className?: string;
}

export interface RichTextHeaderProps {
  variant?: 'sm' | 'lg';
  children: ReactNode;
  className?: string;
}

export interface RichTextContentProps {
  children: ReactNode;
  className?: string;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * RichText.Header
 * variant="lg" (default) → headline040 — 28px bold  (Figma: header 2)
 * variant="sm"           → headline025 — 20px bold  (Figma: header 1)
 */
const Header: FC<RichTextHeaderProps> = ({ variant = 'lg', children, className }) => (
  <Typography
    as="h2"
    variant={variant === 'lg' ? 'headline040' : 'headline025'}
    className={twMerge('text-[--rich-text-header] w-full', className)}
  >
    {children}
  </Typography>
);

/**
 * RichText.Content
 * Wraps body copy in paragraph020 (14px / leading-20) styles.
 * Consecutive <p> tags receive bottom margin via the
 * [&_p:not(:last-child)] selector so consumers just drop <p> tags in.
 */
const Content: FC<RichTextContentProps> = ({ children, className }) => (
  <Typography
    as="div"
    variant="paragraph020"
    className={twMerge(
      'w-full text-[--rich-text-body] [&_p:not(:last-child)]:mb-[--padding-sm]',
      className
    )}
  >
    {children}
  </Typography>
);

// ─── Compound component type ──────────────────────────────────────────────────

type RichTextComponent = FC<RichTextProps> & {
  Header: typeof Header;
  Content: typeof Content;
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const RichText: RichTextComponent = ({ children, className }) => (
  <div className={twMerge('flex flex-col gap-[--padding-lg] items-start w-full', className)}>
    {children}
  </div>
);

RichText.Header = Header;
RichText.Content = Content;
